/* eslint-disable max-lines */
import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { MappingLevel } from "@src/appV2/Accounts/Documents/types";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  isCapacitorPlatform,
  useToast,
} from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { useIsDeviceLocationPermissionGranted } from "@src/appV2/Location/deviceLocation/geoLocation";
import {
  type BackgroundCheckMissingDataType,
  BookabilityUnmetCriteria,
  type BookabilityUnmetCriteriaWithMetadata,
} from "@src/appV2/OpenShifts/ShiftAction/types";
import { createBackgroundCheckUrlFromMissingDataTypes } from "@src/appV2/redesign/Shift/createBackgroundCheckUrlFromMissingDataTypes";
import {
  type AcceptOrDeclineShiftInviteErrorResponse,
  type AcceptOrDeclineShiftInviteResponse,
  type AcceptShiftInviteProps,
  type MissingDocument,
  shiftInvitesErrorSchema,
} from "@src/appV2/redesign/WorkerShifts/ShiftInvites/types";
import { type AxiosError } from "axios";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

import { ShiftInviteErrors } from "../constant";
import { useShiftInviteItemContext } from "../context/shiftInviteItemContext";
import { getConflictingShift, getConflictingShiftInvites } from "../utils";
import { logShiftInviteConflictEvent } from "./shiftInviteActionHelper";
import { useAcceptOrDeclineShiftInvite } from "./useAcceptOrDeclineShiftInvite";
import { useWorkerPendingShiftInvites } from "./useWorkerPendingShiftInvites";
import { useWorkerUpcomingShifts } from "./useWorkerUpcomingShifts";

export function useShiftInviteActions() {
  const {
    mutateAsync: mutateAcceptOrDecline,
    isLoading: isLoadingAcceptOrDecline,
    variables: acceptOrDeclineRequest,
    isSuccess: isSuccessAcceptOrDecline,
  } = useAcceptOrDeclineShiftInvite();
  const { refetch: refreshPendingInvites, data: pendingShiftInvites } =
    useWorkerPendingShiftInvites();
  const { data: upcomingShifts, isLoading: isLoadingUpcomingShifts } = useWorkerUpcomingShifts({
    upcoming: true,
  });
  const history = useHistory();
  const {
    data: isDeviceLocationPermissionGranted,
    isSuccess: isDeviceLocationPermissionGrantedSuccess,
  } = useIsDeviceLocationPermissionGranted();

  const geolocationIsUnavailable =
    isDeviceLocationPermissionGrantedSuccess &&
    isDeviceLocationPermissionGranted === false &&
    isCapacitorPlatform();

  const { showSuccessToast, showErrorToast } = useToast();

  const {
    setConflictingShift,
    conflictingInvites,
    setConflictingInvites,
    conflictingShiftDialogState,
    conflictingInviteDialogState,
    overworkingShiftDialogState,
    locationPermissionsModalState,
    missingFacilityRequiredDocumentsDialogState,
    missingCoreRequiredDocumentsDialogState,
    setMissingDocuments,
    weeklyHoursLimitExceededDialogState,
    setWeeklyHoursLimit,
    resetWarnings,
  } = useShiftInviteItemContext();

  interface HandleShiftInviteErrorProps {
    error: AxiosError;
    status: ShiftInviteStatus;
    metadata: {
      shiftInviteId: string;
    };
  }

  function handleMissingDocuments(missingDocuments: MissingDocument[]): boolean {
    if (missingDocuments?.every((missingDocument) => missingDocument.level === MappingLevel.HCF)) {
      setMissingDocuments(missingDocuments.map((missingDocument) => missingDocument.name));
      missingFacilityRequiredDocumentsDialogState.openModal();
      return true;
    }

    if (missingDocuments) {
      setMissingDocuments(
        missingDocuments
          .filter((missingDocument) => missingDocument.level !== MappingLevel.HCF)
          .map((coreDocument) => coreDocument.name)
      );
      missingCoreRequiredDocumentsDialogState.openModal();
      return true;
    }

    return false;
  }

  function handleUnmetCriteria(params: {
    unmetCriteria: BookabilityUnmetCriteria | undefined;
    status: ShiftInviteStatus;
    reasonsWithMetadata: BookabilityUnmetCriteriaWithMetadata[] | undefined;
    metadata: {
      shiftInviteId: string;
    };
  }) {
    const { unmetCriteria, status, reasonsWithMetadata, metadata } = params;
    if (unmetCriteria === BookabilityUnmetCriteria.SHIFT_OVERWORKING) {
      overworkingShiftDialogState.openModal();
      return true;
    }

    if (unmetCriteria === BookabilityUnmetCriteria.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK) {
      let missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[] | undefined;

      if (isDefined(reasonsWithMetadata)) {
        const workerIsMissingDataForBackgroundCheckReasonWithMetadata = reasonsWithMetadata.find(
          (reason) =>
            reason.unmetCriteria ===
            BookabilityUnmetCriteria.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK
        );

        if (isDefined(workerIsMissingDataForBackgroundCheckReasonWithMetadata)) {
          missingBackgroundCheckDataTypes =
            workerIsMissingDataForBackgroundCheckReasonWithMetadata.metadata.missingDataTypes;
        }
      }

      const url = createBackgroundCheckUrlFromMissingDataTypes({
        queryParams: new URLSearchParams(history.location.search),
        returnTo: "shiftInvite",
        missingBackgroundCheckDataTypes,
      });
      history.push(url);
      return true;
    }

    if (
      unmetCriteria === BookabilityUnmetCriteria.SHIFT_STARTING_SOON_WITHOUT_VALID_BACKGROUND_CHECK
    ) {
      showErrorToast(
        "Your background check is not valid and the shift is starting soon. Please contact support so that we can assist you to accept the invite."
      );
      logEvent(
        APP_V2_APP_EVENTS.FAILURE_TO_ACCEPT_SHIFT_INVITE_STARTING_SOON_BECAUSE_OF_BACKGROUND_CHECK,
        {
          ...metadata,
        }
      );
      return true;
    }

    let errorMessage = `Failed to update shift invite. Please try again later.`;

    if (isDefined(unmetCriteria) && unmetCriteria in ShiftInviteErrors) {
      errorMessage = ShiftInviteErrors[unmetCriteria];
    } else if (status === ShiftInviteStatus.ACCEPTED) {
      errorMessage = `Failed to accept shift invite. Please try again later.`;
    } else if (status === ShiftInviteStatus.DECLINED) {
      errorMessage = `Failed to decline shift invite. Please try again later.`;
    }

    showErrorToast(errorMessage);

    return false;
  }

  function handleWeeklyHoursLimitExceeded(weeklyHoursLimit: number) {
    setWeeklyHoursLimit(weeklyHoursLimit);
    weeklyHoursLimitExceededDialogState.openModal();
  }

  function handleShiftInviteError(handleShiftInviteErrorProps: HandleShiftInviteErrorProps) {
    const { error, status, metadata } = handleShiftInviteErrorProps;
    if (isDefined(error?.response)) {
      const parsedErrorResult = shiftInvitesErrorSchema.safeParse(error.response.data);

      if (parsedErrorResult.success) {
        const data = error?.response?.data as AcceptOrDeclineShiftInviteErrorResponse;
        const unmetCriteria = data?.errors?.[0]?.reasons?.[0];
        const missingDocuments = data?.errors?.[0]?.missingDocuments;
        const weeklyHoursRestriction = data?.errors?.[0]?.weeklyHoursRestriction;
        const reasonsWithMetadata = data?.errors?.[0]?.reasonsWithMetadata;

        if (isDefined(weeklyHoursRestriction) && weeklyHoursRestriction.exceeded) {
          handleWeeklyHoursLimitExceeded(weeklyHoursRestriction.limit);
          return;
        }

        if (isDefined(missingDocuments) && handleMissingDocuments(missingDocuments)) {
          return;
        }

        if (
          handleUnmetCriteria({
            unmetCriteria,
            status,
            reasonsWithMetadata,
            metadata,
          })
        ) {
          return;
        }
      } else {
        logError(APP_V2_APP_EVENTS.SHIFT_INVITE_SCHEMA_VALIDATION_ERROR, { error });
      }
    }

    // reset the warnings if shift invite accept errors due to an unknown reason
    resetWarnings();
  }

  async function declineConflictingInvites() {
    if (conflictingInvites.length > 0) {
      const pluralizedInvite = pluralize("invite", conflictingInvites.length);
      try {
        await Promise.all(
          conflictingInvites.map(
            async (invite) =>
              await mutateAcceptOrDecline({
                shiftInviteId: invite.id,
                status: ShiftInviteStatus.DECLINED,
              })
          )
        );
        showSuccessToast(
          `The conflicting shift ${pluralizedInvite} ${pluralize(
            "was",
            conflictingInvites.length
          )} ${ShiftInviteStatus.DECLINED.toLocaleLowerCase()} successfully.`
        );
      } catch (error) {
        const axiosError = error as AxiosError;
        if (isDefined(axiosError?.response)) {
          const parsedErrorResult = shiftInvitesErrorSchema.safeParse(axiosError.response.data);

          if (parsedErrorResult.success) {
            showErrorToast(
              `The conflicting shift ${pluralizedInvite} could not be ${ShiftInviteStatus.DECLINED.toLocaleLowerCase()}, please try again manually later.`
            );
          } else {
            logError(APP_V2_APP_EVENTS.SHIFT_INVITE_SCHEMA_VALIDATION_ERROR, { error });
          }
        }
      }
    }
  }

  async function acceptShiftInvite(
    acceptShiftInviteProps: AcceptShiftInviteProps
  ): Promise<AcceptOrDeclineShiftInviteResponse | undefined> {
    if (geolocationIsUnavailable) {
      locationPermissionsModalState.openModal();
      return undefined;
    }

    const {
      shiftInvite,
      isOverworkingShiftsAllowed = false,
      shouldDeclineConflictingInvites = false,
      skipFacilityRequiredDocumentCheck = false,
      computedArrivalData,
      isShortLeadTime = false,
    } = acceptShiftInviteProps;
    const {
      id: shiftInviteId,
      attributes: { shiftDetails: shiftInviteDetails, workplaceId },
    } = shiftInvite;
    const result = getConflictingShift(upcomingShifts ?? [], {
      ...shiftInviteDetails,
      facilityId: workplaceId,
    });
    if (isDefined(result)) {
      setConflictingShift(result);
      logShiftInviteConflictEvent(shiftInviteId, result, shiftInviteDetails);
      conflictingShiftDialogState.openModal();
      return undefined;
    }

    const conflictingInviteResults = getConflictingShiftInvites(
      (pendingShiftInvites?.data ?? []).filter((invite) => invite.id !== shiftInviteId),
      shiftInvite
    );
    if (conflictingInviteResults.length > 0 && !shouldDeclineConflictingInvites) {
      setConflictingInvites(conflictingInviteResults);
      conflictingInviteDialogState.openModal();
      return undefined;
    }

    const computedArrival = isDefined(computedArrivalData)
      ? {
          durationInMinutes: computedArrivalData.durationInMinutes,
          distanceInMeters: computedArrivalData.distanceInMeters,
          gracePeriodInMins: computedArrivalData.gracePeriodInMinutes,
          targetArrivalTime: computedArrivalData.targetArrivalTime.toISOString(),
          provider: computedArrivalData.provider,
        }
      : undefined;

    try {
      const response = await mutateAcceptOrDecline({
        shiftInviteId,
        status: ShiftInviteStatus.ACCEPTED,
        isOverworkingShiftsAllowed,
        skipFacilityRequiredDocumentCheck,
        computedArrival,
      });

      logEvent(APP_V2_USER_EVENTS.SHIFT_INVITE_ACCEPTED, {
        shiftInviteId,
        isShortLeadTime,
        isTargetArrivalTimeDefined: isDefined(computedArrival?.targetArrivalTime),
        computedArrival,
      });

      if (!isShortLeadTime) {
        showSuccessToast(
          `Shift invite ${ShiftInviteStatus.ACCEPTED.toLocaleLowerCase()} successfully.`
        );
      }

      if (shouldDeclineConflictingInvites) {
        await declineConflictingInvites();
      }

      await refreshPendingInvites();
      return response;
    } catch (error) {
      handleShiftInviteError({
        error: error as AxiosError,
        status: ShiftInviteStatus.ACCEPTED,
        metadata: {
          shiftInviteId: shiftInvite.id,
        },
      });

      await refreshPendingInvites();
      return undefined;
    }
  }

  interface DeclineShiftInviteProps {
    shiftInviteId: string;
  }
  async function declineShiftInvite(declineShiftInviteProps: DeclineShiftInviteProps) {
    const { shiftInviteId } = declineShiftInviteProps;
    try {
      await mutateAcceptOrDecline({ shiftInviteId, status: ShiftInviteStatus.DECLINED });
      showSuccessToast(
        `Shift invite ${ShiftInviteStatus.DECLINED.toLocaleLowerCase()} successfully.`
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      if (isDefined(axiosError?.response)) {
        const parsedErrorResult = shiftInvitesErrorSchema.safeParse(axiosError.response.data);

        if (parsedErrorResult.success) {
          showErrorToast(
            `This invite could not be ${ShiftInviteStatus.DECLINED.toLocaleLowerCase()}, please try again later.`
          );
        } else {
          logError(APP_V2_APP_EVENTS.SHIFT_INVITE_SCHEMA_VALIDATION_ERROR, { error });
        }
      }
    }

    await refreshPendingInvites();
  }

  return {
    acceptShiftInvite,
    declineShiftInvite,
    isLoadingAcceptOrDecline,
    isSuccessAcceptOrDecline,
    acceptOrDeclineRequest,
    isLoadingUpcomingShifts,
  };
}
/* eslint-enable max-lines */
